import React, { useState, useEffect, useRef, useMemo } from 'react';
import {
  Button,
  Modal,
  Form,
  Input,
  Select,
  TimePicker,
  message,
  Tag,
  Space,
  Typography,
  Popconfirm,
  Badge,
  Divider,
  Switch,
  notification,
  AutoComplete,
  Tooltip,
  Drawer,
} from 'antd';
import {
  PlusOutlined,
  DeleteOutlined,
  EditOutlined,
  ReloadOutlined,
  PhoneOutlined,
  BellOutlined,
  EnvironmentOutlined,
  SearchOutlined,
  SettingOutlined,
  SaveOutlined,
  CloseOutlined,
  UndoOutlined,
} from '@ant-design/icons';
import { supabase } from '../../api/supabase';
import { colors, spacing, radius, shadows } from '../../styles/monochromeTheme';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import 'dayjs/locale/fr';

// Leaflet imports
import { MapContainer, TileLayer, Marker, Popup, Polyline, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

dayjs.extend(relativeTime);
dayjs.locale('fr');

const { Title, Text } = Typography;
const { TextArea } = Input;
const { Option } = Select;

// Fix Leaflet default marker icon issue
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
});

// Custom boat icon
const createBoatIcon = (color, isUrgent = false, isTransit = false) => {
  return L.divIcon({
    className: 'custom-boat-marker',
    html: `
      <div style="
        background: ${isUrgent ? '#ff4d4f' : color};
        color: white;
        width: ${isTransit ? '36px' : '32px'};
        height: ${isTransit ? '36px' : '32px'};
        border-radius: 50%;
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: ${isTransit ? '18px' : '16px'};
        box-shadow: 0 2px 8px ${color}80;
        border: 2px solid white;
        ${isUrgent ? 'animation: pulse 1.5s ease-in-out infinite;' : ''}
        ${isTransit ? 'animation: bounce 1s ease-in-out infinite;' : ''}
      ">🚤</div>
    `,
    iconSize: [isTransit ? 36 : 32, isTransit ? 36 : 32],
    iconAnchor: [isTransit ? 18 : 16, isTransit ? 18 : 16],
    popupAnchor: [0, -16],
  });
};

// Custom location marker icon
const createLocationIcon = (color, label, isConfig = false) => {
  return L.divIcon({
    className: 'custom-location-marker',
    html: `
      <div style="
        display: flex;
        flex-direction: column;
        align-items: center;
        cursor: ${isConfig ? 'move' : 'pointer'};
      ">
        <div style="
          background: ${color};
          color: white;
          padding: 4px 8px;
          border-radius: 12px;
          font-size: 10px;
          font-weight: 600;
          white-space: nowrap;
          box-shadow: 0 2px 6px rgba(0,0,0,0.3);
          ${isConfig ? 'border: 2px dashed white;' : ''}
        ">${label}${isConfig ? ' ✋' : ''}</div>
        <div style="
          width: ${isConfig ? '12px' : '8px'};
          height: ${isConfig ? '12px' : '8px'};
          background: ${color};
          border-radius: 50%;
          margin-top: 2px;
          border: 2px solid white;
          box-shadow: 0 2px 4px rgba(0,0,0,0.2);
        "></div>
      </div>
    `,
    iconSize: [80, 40],
    iconAnchor: [40, 40],
  });
};

// Default physical locations with real geographic coordinates
const DEFAULT_LOCATIONS = [
  { value: 'MASTERSKI', label: 'Masterski', color: '#1890ff', lat: 18.0280, lng: -63.0850 },
  { value: 'ANSE_MARCEL', label: 'Anse Marcel', color: '#597ef7', lat: 18.1167, lng: -63.0333 },
  { value: 'MFL', label: 'MFL', color: '#52c41a', lat: 17.9350, lng: -62.9200 },
  { value: 'CREOLE', label: 'Creole', color: '#13c2c2', lat: 17.9100, lng: -62.8800 },
  { value: 'FOURCHUE', label: 'Fourchue', color: '#fa541c', lat: 17.9400, lng: -62.8700 },
  { value: 'GUSTAVIA', label: 'Gustavia', color: '#2f54eb', lat: 17.8962, lng: -62.8519 },
  { value: 'BOBBYS', label: "Bobby's", color: '#722ed1', lat: 17.8980, lng: -62.8480 },
  { value: 'ANGUILLA', label: 'Anguilla', color: '#eb2f96', lat: 18.2206, lng: -63.0686 },
];

const LOCATIONS_CONFIG_KEY = 'transfer_locations_config';

// Transit states (boats in movement between locations)
// SXM/SBH: EN_VOL → ATTERRI → VERS_MFL (from airport) → VERS_FOURCHUE/GUSTAVIA
// SBH/SXM: MASTERSKI → CREOLE → VERS_MFL (from Creole) → VERS_MASTERSKI
const TRANSIT_STATES = [
  // États avion
  { value: 'EN_VOL', label: '✈️ En vol', color: '#faad14', icon: '✈️' },
  { value: 'ATTERRI', label: '🛬 Atterri', color: '#52c41a', icon: '🛬' },

  // Direction SBH (SXM → SBH) - coming from airport
  { value: 'VERS_MFL', label: '🚤 Aéroport → MFL', color: '#52c41a', from: 'MASTERSKI', to: 'MFL' },
  { value: 'VERS_FOURCHUE', label: '🚤 → Fourchue', color: '#fa541c', from: 'MFL', to: 'FOURCHUE' },
  { value: 'VERS_GUSTAVIA', label: '🚤 → Gustavia', color: '#2f54eb', from: 'FOURCHUE', to: 'GUSTAVIA' },
  { value: 'VERS_BOBBYS', label: '🚤 → Bobby\'s', color: '#722ed1', from: 'GUSTAVIA', to: 'BOBBYS' },

  // Direction SXM (SBH → SXM) - coming from Creole
  { value: 'VERS_CREOLE', label: '🚤 → Creole', color: '#13c2c2', from: 'MASTERSKI', to: 'CREOLE' },
  { value: 'VERS_MFL_CREOLE', label: '🚤 Creole → MFL', color: '#52c41a', from: 'CREOLE', to: 'MFL' },
  { value: 'VERS_MASTERSKI', label: '🚤 → Masterski', color: '#1890ff', from: 'MFL', to: 'MASTERSKI' },

  // Anguilla
  { value: 'VERS_ANGUILLA', label: '🚤 → Anguilla', color: '#eb2f96', from: 'MASTERSKI', to: 'ANGUILLA' },

  // Terminé
  { value: 'TERMINE', label: '✅ Terminé', color: '#52c41a', icon: '✅' },
];

// Map center (between Saint-Martin and Saint-Barth)
const MAP_CENTER = [17.98, -62.95];
const MAP_ZOOM = 10;

const TRANSFER_ROUTES = ['SXM/SBH', 'SBH/SXM', 'AXA/SXM', 'SXM/AXA', 'AXA/SBH', 'SBH/AXA', 'Marcel/SBH', 'SBH/Marcel'];

const TRANSPORT_SXM_OPTIONS = [
  { value: 'VIP_GEORGES', label: 'VIP Georges' },
  { value: 'TAXI_GEORGES', label: 'Taxi Georges' },
  { value: 'AUTRE', label: 'Autre' },
];

const TRANSPORT_SBH_OPTIONS = [
  { value: 'TAXI', label: 'Taxi' },
  { value: 'TIERS', label: 'Tiers' },
  { value: 'AUTRE', label: 'Autre' },
  { value: 'CLIENT', label: 'Client' },
];

// Important contacts for quick calls
const IMPORTANT_CONTACTS = [
  { id: 'GENDARMERIE', label: 'Gendarmerie Maritime', phone: '+590590276060', icon: '🚔' },
  { id: 'CAPITAINERIE_GUSTAVIA', label: 'Capitainerie Gustavia', phone: '+590590277066', icon: '⚓' },
  { id: 'CAPITAINERIE_FOURCHUE', label: 'Capitainerie Fourchue', phone: '+590590278888', icon: '⚓' },
];

// WhatsApp group for transfer notifications (can be configured)
const WHATSAPP_GROUP_CONFIG_KEY = 'transfer_whatsapp_group';

// Helper functions for WhatsApp
const openWhatsAppCall = (phone) => {
  const cleanPhone = phone.replace(/[^0-9+]/g, '');
  window.open(`https://wa.me/${cleanPhone}`, '_blank');
};

const sendWhatsAppMessage = (phone, message) => {
  const cleanPhone = phone.replace(/[^0-9+]/g, '');
  const encodedMessage = encodeURIComponent(message);
  window.open(`https://wa.me/${cleanPhone}?text=${encodedMessage}`, '_blank');
};

// Get contextual action buttons based on current location
const getContextualActions = (transfer, locations) => {
  const actions = [];
  const loc = transfer?.current_location;

  // Add Gendarmerie Maritime call for specific states
  if (['VERS_MFL', 'VERS_FOURCHUE', 'VERS_GUSTAVIA', 'VERS_MASTERSKI'].includes(loc)) {
    actions.push({
      type: 'call',
      label: 'Gendarmerie Maritime',
      phone: IMPORTANT_CONTACTS.find(c => c.id === 'GENDARMERIE')?.phone,
      icon: '🚔',
      color: '#722ed1',
    });
  }

  // Add hotel/concierge call if available
  if (transfer?.hotel_contacts?.length > 0 && ['VERS_FOURCHUE', 'VERS_GUSTAVIA', 'VERS_BOBBYS'].includes(loc)) {
    transfer.hotel_contacts.forEach((contact, idx) => {
      if (contact.phone) {
        actions.push({
          type: 'call',
          label: contact.name || `Contact ${idx + 1}`,
          phone: contact.phone,
          icon: '🏨',
          color: '#1890ff',
        });
      }
    });
  }

  // Add transport contact if available
  if (transfer?.transport_contact && ['ATTERRI', 'VERS_MFL', 'VERS_MASTERSKI'].includes(loc)) {
    actions.push({
      type: 'call',
      label: 'Transport',
      phone: transfer.transport_contact,
      icon: '🚗',
      color: '#52c41a',
    });
  }

  // Add notification messages based on state
  if (loc === 'ATTERRI') {
    actions.push({
      type: 'message',
      label: 'Notifier atterrissage',
      message: `✈️ ${transfer?.client_name} a atterri${transfer?.landing_time ? ` à ${transfer.landing_time}` : ''} - ${transfer?.yacht_name || 'Bateau'}`,
      icon: '📢',
      color: '#faad14',
    });
  }

  if (loc === 'VERS_MFL') {
    actions.push({
      type: 'message',
      label: 'Notifier départ MFL',
      message: `🚤 ${transfer?.client_name} est en direction de MFL - ${transfer?.yacht_name || 'Bateau'}`,
      icon: '📢',
      color: '#52c41a',
    });
  }

  if (loc === 'VERS_FOURCHUE') {
    actions.push({
      type: 'message',
      label: 'Notifier passage Fourchue',
      message: `🏝️ ${transfer?.client_name} passe Fourchue - ${transfer?.yacht_name || 'Bateau'}`,
      icon: '📢',
      color: '#fa541c',
    });
    // Add Capitainerie Fourchue call
    actions.push({
      type: 'call',
      label: 'Capitainerie Fourchue',
      phone: IMPORTANT_CONTACTS.find(c => c.id === 'CAPITAINERIE_FOURCHUE')?.phone,
      icon: '⚓',
      color: '#13c2c2',
    });
  }

  if (loc === 'VERS_GUSTAVIA') {
    actions.push({
      type: 'message',
      label: 'Notifier arrivée Gustavia',
      message: `⛵ ${transfer?.client_name} arrive à Gustavia - ${transfer?.yacht_name || 'Bateau'}`,
      icon: '📢',
      color: '#2f54eb',
    });
    // Add Capitainerie Gustavia call
    actions.push({
      type: 'call',
      label: 'Capitainerie Gustavia',
      phone: IMPORTANT_CONTACTS.find(c => c.id === 'CAPITAINERIE_GUSTAVIA')?.phone,
      icon: '⚓',
      color: '#2f54eb',
    });
  }

  return actions;
};

const NOTIFICATION_SOUND = 'data:audio/wav;base64,UklGRnoGAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQoGAACBhYqFbF1fdJivrJBhNjVgodDbq2EcBj+a2/LDciUFLIHO8tiJNwgZaLvt559NEAxQp+PwtmMcBjiR1/LMeSwFJHfH8N2QQAoUXrTp66hVFApGn+DyvmwhBTGH0fPTgjMGHm7A7+OZURE=';

const TRACKED_TRANSFERS_KEY = 'tracked_transfers';

// Draggable marker component for config mode
const DraggableMarker = ({ location, onDragEnd, configMode }) => {
  const markerRef = useRef(null);

  const eventHandlers = useMemo(
    () => ({
      dragend() {
        const marker = markerRef.current;
        if (marker != null) {
          const pos = marker.getLatLng();
          onDragEnd(location.value, pos.lat, pos.lng);
        }
      },
    }),
    [location.value, onDragEnd],
  );

  return (
    <Marker
      draggable={configMode}
      eventHandlers={eventHandlers}
      position={[location.lat, location.lng]}
      ref={markerRef}
      icon={createLocationIcon(location.color, location.label, configMode)}
    >
      <Popup>
        <div style={{ textAlign: 'center' }}>
          <strong>{location.label}</strong>
          {configMode && (
            <div style={{ fontSize: 11, color: '#666', marginTop: 4 }}>
              📍 {location.lat.toFixed(4)}, {location.lng.toFixed(4)}
              <br />
              <span style={{ color: '#1890ff' }}>Glissez pour repositionner</span>
            </div>
          )}
        </div>
      </Popup>
    </Marker>
  );
};

// Transit marker component - positioned at midpoint of route
const TransitMarker = ({ transfer, fromLoc, toLoc, color, isUrgent, onClick, clientName }) => {
  // Position at midpoint of the route
  const midLat = (fromLoc.lat + toLoc.lat) / 2;
  const midLng = (fromLoc.lng + toLoc.lng) / 2;

  return (
    <>
      {/* Route line */}
      <Polyline
        positions={[[fromLoc.lat, fromLoc.lng], [toLoc.lat, toLoc.lng]]}
        pathOptions={{ color: color, weight: 2, dashArray: '5, 10', opacity: 0.6 }}
      />
      {/* Transit marker with client name */}
      <Marker
        position={[midLat, midLng]}
        icon={L.divIcon({
          className: 'custom-boat-marker',
          html: `
            <div style="
              background: ${isUrgent ? '#ff4d4f' : color};
              color: white;
              padding: 6px 10px;
              border-radius: 12px;
              font-size: 12px;
              font-weight: 600;
              display: flex;
              align-items: center;
              gap: 4px;
              box-shadow: 0 2px 8px ${color}80;
              white-space: nowrap;
              border: 2px solid white;
            ">
              🚤 ${clientName}
            </div>
          `,
          iconSize: [120, 30],
          iconAnchor: [60, 15],
        })}
        eventHandlers={{ click: onClick }}
      >
        <Popup>
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontWeight: 600, marginBottom: 4 }}>{clientName}</div>
            <div style={{ fontSize: 12, color: '#666' }}>{transfer.yacht_name}</div>
            <Tag color={color} style={{ marginTop: 4 }}>En transit</Tag>
          </div>
        </Popup>
      </Marker>
    </>
  );
};

// Map bounds setter component with resize handling
const SetMapBounds = () => {
  const map = useMap();

  useEffect(() => {
    // Set bounds to show all three islands
    const bounds = [
      [17.85, -63.15], // Southwest
      [18.25, -62.80], // Northeast
    ];
    map.fitBounds(bounds, { padding: [20, 20] });

    // Handle resize events - invalidate size so Leaflet recalculates
    const handleResize = () => {
      setTimeout(() => {
        map.invalidateSize();
      }, 100);
    };

    window.addEventListener('resize', handleResize);

    // Also observe container resize using ResizeObserver
    const container = map.getContainer();
    const resizeObserver = new ResizeObserver(() => {
      map.invalidateSize();
    });
    resizeObserver.observe(container);

    return () => {
      window.removeEventListener('resize', handleResize);
      resizeObserver.disconnect();
    };
  }, [map]);

  return null;
};

const TransferTracking = () => {
  const [transfers, setTransfers] = useState([]);
  const [yachts, setYachts] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingTransfer, setEditingTransfer] = useState(null);
  const [detailTransfer, setDetailTransfer] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [hoveredTransferId, setHoveredTransferId] = useState(null);
  const [form] = Form.useForm();
  const [notificationsEnabled, setNotificationsEnabled] = useState(false);
  const [hasPermission, setHasPermission] = useState(false);
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [trackedTransfers, setTrackedTransfers] = useState([]);
  const [urgentTransfers, setUrgentTransfers] = useState([]);
  const audioRef = useRef(null);
  const notificationIntervalsRef = useRef({});
  const landingCheckIntervalRef = useRef(null);

  // Configuration mode state
  const [configMode, setConfigMode] = useState(false);
  const [locations, setLocations] = useState(DEFAULT_LOCATIONS);

  // WhatsApp group configuration
  const [whatsappConfigVisible, setWhatsappConfigVisible] = useState(false);
  const [whatsappGroupNumber, setWhatsappGroupNumber] = useState('');

  // Load saved locations from localStorage
  useEffect(() => {
    const savedLocations = localStorage.getItem(LOCATIONS_CONFIG_KEY);
    if (savedLocations) {
      try {
        setLocations(JSON.parse(savedLocations));
      } catch (e) {
        console.error('Error loading locations config:', e);
      }
    }
  }, []);

  // Load saved WhatsApp group number
  useEffect(() => {
    const savedWhatsappGroup = localStorage.getItem(WHATSAPP_GROUP_CONFIG_KEY);
    if (savedWhatsappGroup) {
      setWhatsappGroupNumber(savedWhatsappGroup);
    }
  }, []);

  const saveWhatsappGroupNumber = () => {
    localStorage.setItem(WHATSAPP_GROUP_CONFIG_KEY, whatsappGroupNumber);
    message.success('Numéro WhatsApp groupe sauvegardé');
    setWhatsappConfigVisible(false);
  };

  // All states for dropdown (computed from current locations)
  const ALL_LOCATIONS = useMemo(() => [...locations, ...TRANSIT_STATES], [locations]);

  // Load tracked transfers
  useEffect(() => {
    const savedTracked = localStorage.getItem(TRACKED_TRANSFERS_KEY);
    if (savedTracked) {
      try {
        setTrackedTransfers(JSON.parse(savedTracked));
      } catch (e) {
        console.error('Error loading tracked transfers:', e);
      }
    }
  }, []);

  // Check landing times for tracked transfers
  useEffect(() => {
    const checkLandingTimes = () => {
      const now = dayjs();
      const urgent = [];

      transfers.forEach(transfer => {
        if (!transfer.landing_time || transfer.status === 'completed') return;
        if (!trackedTransfers.includes(transfer.id)) return;

        const [hours, minutes] = transfer.landing_time.split(':');
        const landingTime = dayjs().hour(parseInt(hours)).minute(parseInt(minutes));
        const minutesUntil = landingTime.diff(now, 'minute');

        if (minutesUntil <= 30 && minutesUntil >= -10) {
          urgent.push(transfer.id);

          const notifKey = `landing_${transfer.id}_${minutesUntil}`;
          if ([30, 15, 5, 0].includes(minutesUntil) && !localStorage.getItem(notifKey)) {
            localStorage.setItem(notifKey, 'true');
            showLandingNotification(transfer, minutesUntil);
          }
        }
      });

      setUrgentTransfers(urgent);
    };

    checkLandingTimes();
    landingCheckIntervalRef.current = setInterval(checkLandingTimes, 60000);

    return () => {
      if (landingCheckIntervalRef.current) {
        clearInterval(landingCheckIntervalRef.current);
      }
    };
  }, [transfers, trackedTransfers, notificationsEnabled]);

  const showLandingNotification = (transfer, minutesUntil) => {
    if (!notificationsEnabled || !hasPermission) return;
    playSound();
    const timeText = minutesUntil <= 0 ? 'MAINTENANT' : `dans ${minutesUntil} min`;
    notification.warning({
      message: `✈️ Atterrissage ${timeText}`,
      description: `${transfer.client_name} - Vol ${transfer.flight_number || 'N/A'} à ${transfer.landing_time}`,
      duration: 15,
      icon: <BellOutlined style={{ color: '#ff4d4f' }} />,
    });
    if ('Notification' in window && Notification.permission === 'granted') {
      new Notification(`✈️ Atterrissage ${timeText}`, {
        body: `${transfer.client_name} - ${transfer.yacht_name}`,
        icon: '/favicon.ico',
        requireInteraction: true,
      });
    }
  };

  const toggleTrackTransfer = (transferId) => {
    setTrackedTransfers(prev => {
      const newTracked = prev.includes(transferId)
        ? prev.filter(id => id !== transferId)
        : [...prev, transferId];
      localStorage.setItem(TRACKED_TRANSFERS_KEY, JSON.stringify(newTracked));
      return newTracked;
    });
  };

  useEffect(() => {
    fetchTransfers();
    fetchYachts();
    fetchEmployees();
    checkNotificationPermission();
    const savedPref = localStorage.getItem('transferNotifications');
    if (savedPref === 'true') setNotificationsEnabled(true);

    const subscription = supabase
      .channel('transfers_realtime')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'transfers' }, () => fetchTransfers())
      .subscribe();

    return () => {
      subscription.unsubscribe();
      Object.values(notificationIntervalsRef.current).forEach(clearTimeout);
    };
  }, []);

  useEffect(() => {
    if (notificationsEnabled) setupNotifications();
  }, [transfers, notificationsEnabled]);

  const checkNotificationPermission = async () => {
    if ('Notification' in window) {
      if (Notification.permission === 'granted') setHasPermission(true);
      else if (Notification.permission !== 'denied') {
        const permission = await Notification.requestPermission();
        setHasPermission(permission === 'granted');
      }
    }
  };

  const toggleNotifications = async () => {
    if (!hasPermission && !notificationsEnabled) {
      await checkNotificationPermission();
      if (!hasPermission) { message.warning('Autorisez les notifications'); return; }
    }
    const newState = !notificationsEnabled;
    setNotificationsEnabled(newState);
    localStorage.setItem('transferNotifications', newState.toString());
  };

  const playSound = () => audioRef.current?.play().catch(() => {});

  const showNotification = (title, body) => {
    if (!notificationsEnabled || !hasPermission) return;
    playSound();
    notification.warning({ message: title, description: body, duration: 10, icon: <BellOutlined style={{ color: '#faad14' }} /> });
    if ('Notification' in window && Notification.permission === 'granted') {
      new Notification(title, { body, icon: '/favicon.ico' });
    }
  };

  const setupNotifications = () => {
    Object.values(notificationIntervalsRef.current).forEach(clearTimeout);
    notificationIntervalsRef.current = {};
    transfers.forEach(transfer => {
      if (transfer.status === 'completed' || !transfer.departure_time) return;
      const departureTime = dayjs(transfer.departure_time);
      const now = dayjs();
      const creoleTime = departureTime.add(45, 'minute');
      if (creoleTime.isAfter(now) && transfer.current_location !== 'CREOLE') {
        notificationIntervalsRef.current[`${transfer.id}_creole`] = setTimeout(() => {
          showNotification(`🚤 ${transfer.yacht_name} - Creole`, `${transfer.client_name} approche Creole`);
        }, creoleTime.diff(now));
      }
      if (transfer.mfl_departure_time) {
        const fourchueTime = dayjs(transfer.mfl_departure_time).add(45, 'minute');
        if (fourchueTime.isAfter(now) && transfer.current_location !== 'FOURCHUE') {
          notificationIntervalsRef.current[`${transfer.id}_fourchue`] = setTimeout(() => {
            showNotification(`🚤 ${transfer.yacht_name} - Fourchue`, `${transfer.client_name} approche Fourchue`);
          }, fourchueTime.diff(now));
        }
      }
    });
  };

  const fetchTransfers = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase.from('transfers').select('*')
        .gte('transfer_date', dayjs().startOf('day').toISOString())
        .order('landing_time', { ascending: true });
      if (error) throw error;
      setTransfers(data || []);
    } catch (error) { message.error('Erreur chargement'); }
    setLoading(false);
  };

  const fetchYachts = async () => {
    const { data } = await supabase.from('yachts').select('id, name, brand').order('name');
    setYachts(data || []);
  };

  const fetchEmployees = async () => {
    const { data } = await supabase.from('employees').select('id, name, first_name, role').eq('status', 'active').order('name');
    setEmployees(data || []);
  };

  const handleAdd = () => {
    setEditingTransfer(null);
    form.resetFields();
    form.setFieldsValue({ transfer_date: dayjs().format('YYYY-MM-DD'), current_location: 'EN_VOL', status: 'pending' });
    setModalVisible(true);
  };

  const handleEdit = (transfer) => {
    setEditingTransfer(transfer);
    form.setFieldsValue({
      ...transfer,
      landing_time: transfer.landing_time ? dayjs(transfer.landing_time, 'HH:mm') : null,
    });
    setModalVisible(true);
  };

  const handleDelete = async (id) => {
    await supabase.from('transfers').delete().eq('id', id);
    message.success('Supprimé');
    fetchTransfers();
  };

  const handleSave = async () => {
    try {
      const values = await form.validateFields();
      const yacht = yachts.find(y => y.id === values.yacht_id);
      const transferData = {
        ...values,
        yacht_name: yacht ? `${yacht.brand} ${yacht.name}` : values.yacht_name,
        landing_time: values.landing_time ? values.landing_time.format('HH:mm') : null,
        transfer_date: values.transfer_date || dayjs().format('YYYY-MM-DD'),
      };

      if (editingTransfer) {
        await supabase.from('transfers').update({ ...transferData, updated_at: new Date().toISOString() }).eq('id', editingTransfer.id);
      } else {
        await supabase.from('transfers').insert([transferData]);
      }
      message.success(editingTransfer ? 'Modifié' : 'Créé');
      setModalVisible(false);
      form.resetFields();
      fetchTransfers();
    } catch (error) { message.error(`Erreur: ${error.message}`); }
  };

  const updateLocation = async (id, location) => {
    const updates = { current_location: location };
    const transfer = transfers.find(t => t.id === id);

    const transitState = TRANSIT_STATES.find(s => s.value === location);
    if (transitState?.from && !transfer.departure_time) {
      updates.departure_time = new Date().toISOString();
    }

    if (location === 'VERS_CREOLE' && transfer.current_location === 'MFL') {
      updates.mfl_departure_time = new Date().toISOString();
    }

    if (location === 'TERMINE' || location === 'BOBBYS' || location === 'GUSTAVIA') {
      updates.status = 'completed';
    }

    await supabase.from('transfers').update(updates).eq('id', id);
    fetchTransfers();
  };

  // Configuration mode handlers
  const handleLocationDragEnd = (locationValue, newLat, newLng) => {
    setLocations(prev => prev.map(loc =>
      loc.value === locationValue
        ? { ...loc, lat: newLat, lng: newLng }
        : loc
    ));
  };

  const saveLocationConfig = () => {
    localStorage.setItem(LOCATIONS_CONFIG_KEY, JSON.stringify(locations));
    message.success('Positions sauvegardées !');
    setConfigMode(false);
  };

  const resetLocationConfig = () => {
    setLocations(DEFAULT_LOCATIONS);
    localStorage.removeItem(LOCATIONS_CONFIG_KEY);
    message.info('Positions réinitialisées');
  };

  const cancelConfigMode = () => {
    // Reload saved locations or default
    const savedLocations = localStorage.getItem(LOCATIONS_CONFIG_KEY);
    if (savedLocations) {
      try {
        setLocations(JSON.parse(savedLocations));
      } catch (e) {
        setLocations(DEFAULT_LOCATIONS);
      }
    } else {
      setLocations(DEFAULT_LOCATIONS);
    }
    setConfigMode(false);
  };

  const activeTransfers = transfers.filter(t => t.status !== 'completed');
  const completedToday = transfers.filter(t => t.status === 'completed').length;

  const filteredTransfers = activeTransfers.filter(t => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      t.client_name?.toLowerCase().includes(query) ||
      t.yacht_name?.toLowerCase().includes(query) ||
      t.flight_number?.toLowerCase().includes(query) ||
      t.transfer_type?.toLowerCase().includes(query)
    );
  });

  // Get transfers at physical locations
  const transfersByLocation = locations.reduce((acc, loc) => {
    acc[loc.value] = activeTransfers.filter(t => t.current_location === loc.value);
    return acc;
  }, {});

  // Get transfers in transit (moving between locations)
  const transfersInTransit = activeTransfers.filter(t => {
    const state = TRANSIT_STATES.find(s => s.value === t.current_location);
    return state && state.from && state.to;
  });

  // Get transfers at special states (EN_VOL, ATTERRI)
  const transfersSpecialState = activeTransfers.filter(t => {
    return t.current_location === 'EN_VOL' || t.current_location === 'ATTERRI';
  });

  const getSxmTransportLabel = (t) => {
    if (t.transport_sxm === 'AUTRE') return t.transport_sxm_custom || 'Autre';
    return TRANSPORT_SXM_OPTIONS.find(o => o.value === t.transport_sxm)?.label || '-';
  };

  return (
    <div style={{ minHeight: '100vh', background: colors.bgPrimary, padding: spacing.md }}>
      <audio ref={audioRef} src={NOTIFICATION_SOUND} preload="auto" />

      {/* CSS for animations */}
      <style>{`
        @keyframes pulse {
          0%, 100% { transform: scale(1); }
          50% { transform: scale(1.15); }
        }
        @keyframes bounce {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-3px); }
        }
        @keyframes glow {
          0% { box-shadow: 0 0 4px 2px rgba(255, 77, 79, 0.4); }
          100% { box-shadow: 0 0 12px 6px rgba(255, 77, 79, 0.8); }
        }
        .leaflet-container {
          background: #1a365d;
          border-radius: 12px;
        }
        .custom-boat-marker, .custom-location-marker {
          background: transparent;
          border: none;
        }
      `}</style>

      {/* Main Layout: Map 80% | List 20% */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 320px', gap: spacing.md, height: 'calc(100vh - 32px)' }}>

        {/* LEFT: Map Section */}
        <div style={{
          borderRadius: radius.xl,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}>
          {/* Map Header */}
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: spacing.sm,
            padding: spacing.sm,
            background: colors.bgDark,
            borderRadius: radius.lg,
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: spacing.md }}>
              <Title level={4} style={{ margin: 0, color: colors.textInverse }}>🚤 Transferts</Title>
              <Text style={{ color: 'rgba(255,255,255,0.6)' }}>{dayjs().format('dddd DD MMMM')}</Text>
            </div>
            <Space size={8}>
              <Badge count={activeTransfers.length} style={{ background: '#1890ff' }} />
              <Badge count={completedToday} style={{ background: '#52c41a' }} />
              {trackedTransfers.length > 0 && (
                <Tooltip title={`${trackedTransfers.length} transfert(s) suivi(s)`}>
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 4,
                    background: urgentTransfers.length > 0 ? 'rgba(255,77,79,0.2)' : 'rgba(255,255,255,0.1)',
                    padding: '2px 8px',
                    borderRadius: 6,
                    border: urgentTransfers.length > 0 ? '1px solid rgba(255,77,79,0.5)' : 'none',
                  }}>
                    <BellOutlined style={{
                      color: urgentTransfers.length > 0 ? '#ff4d4f' : '#faad14',
                      fontSize: 12,
                      animation: urgentTransfers.length > 0 ? 'pulse 1s infinite' : 'none',
                    }} />
                    <span style={{
                      color: urgentTransfers.length > 0 ? '#ff4d4f' : 'rgba(255,255,255,0.8)',
                      fontSize: 11,
                      fontWeight: 600,
                    }}>
                      {trackedTransfers.length}
                    </span>
                  </div>
                </Tooltip>
              )}
              <div style={{ display: 'flex', alignItems: 'center', gap: 4, background: 'rgba(255,255,255,0.1)', padding: '4px 8px', borderRadius: 6 }}>
                <BellOutlined style={{ color: notificationsEnabled ? '#faad14' : 'rgba(255,255,255,0.4)', fontSize: 12 }} />
                <Switch size="small" checked={notificationsEnabled} onChange={toggleNotifications} />
              </div>
              {/* WhatsApp config button */}
              <Tooltip title="Config WhatsApp groupe">
                <Button
                  size="small"
                  onClick={() => setWhatsappConfigVisible(true)}
                  ghost
                  style={{
                    color: whatsappGroupNumber ? '#25d366' : 'rgba(255,255,255,0.7)',
                    borderColor: whatsappGroupNumber ? '#25d366' : 'rgba(255,255,255,0.3)',
                    fontSize: 14,
                  }}
                >
                  📱
                </Button>
              </Tooltip>
              {/* Config mode toggle */}
              {configMode ? (
                <Space size={4}>
                  <Button size="small" icon={<SaveOutlined />} type="primary" onClick={saveLocationConfig}>
                    Sauver
                  </Button>
                  <Tooltip title="Réinitialiser">
                    <Button size="small" icon={<UndoOutlined />} onClick={resetLocationConfig} />
                  </Tooltip>
                  <Button size="small" icon={<CloseOutlined />} onClick={cancelConfigMode} />
                </Space>
              ) : (
                <Tooltip title="Configurer les positions">
                  <Button
                    size="small"
                    icon={<SettingOutlined />}
                    onClick={() => setConfigMode(true)}
                    ghost
                    style={{ color: 'rgba(255,255,255,0.7)', borderColor: 'rgba(255,255,255,0.3)' }}
                  />
                </Tooltip>
              )}
            </Space>
          </div>

          {/* Config Mode Banner */}
          {configMode && (
            <div style={{
              background: 'rgba(250,173,20,0.15)',
              border: '1px solid rgba(250,173,20,0.4)',
              borderRadius: radius.md,
              padding: spacing.sm,
              marginBottom: spacing.sm,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}>
              <div>
                <Text strong style={{ color: '#faad14' }}>⚙️ Mode Configuration</Text>
                <Text style={{ color: 'rgba(250,173,20,0.8)', fontSize: 11, marginLeft: 8 }}>
                  Glissez les marqueurs pour repositionner les points
                </Text>
              </div>
              <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                {locations.map(loc => (
                  <Tag key={loc.value} color={loc.color} style={{ fontSize: 10, margin: 0 }}>
                    {loc.label}
                  </Tag>
                ))}
              </div>
            </div>
          )}

          {/* Leaflet Map */}
          <div style={{ flex: 1, borderRadius: radius.lg, overflow: 'hidden', minHeight: 400, border: configMode ? '2px dashed #faad14' : 'none', position: 'relative' }}>
            <MapContainer
              center={MAP_CENTER}
              zoom={MAP_ZOOM}
              style={{ height: '100%', width: '100%' }}
              zoomControl={true}
            >
              <SetMapBounds />
              <TileLayer
                attribution='&copy; <a href="https://stadiamaps.com/">Stadia Maps</a>'
                url="https://tiles.stadiamaps.com/tiles/alidade_smooth_dark/{z}/{x}/{y}{r}.png?api_key=d3abc998-86eb-4c84-84fb-a11f7b20c5eb"
              />

              {/* Location markers - draggable in config mode */}
              {locations.map(loc => {
                const hasBoats = transfersByLocation[loc.value]?.length > 0;

                // In config mode, always show draggable markers
                if (configMode) {
                  return (
                    <DraggableMarker
                      key={loc.value}
                      location={loc}
                      onDragEnd={handleLocationDragEnd}
                      configMode={true}
                    />
                  );
                }

                // Normal mode: only show if no boats
                if (hasBoats) return null;

                return (
                  <Marker
                    key={loc.value}
                    position={[loc.lat, loc.lng]}
                    icon={createLocationIcon(loc.color, loc.label, false)}
                  >
                    <Popup>
                      <div style={{ textAlign: 'center' }}>
                        <strong>{loc.label}</strong>
                        <div style={{ fontSize: 11, color: '#666' }}>Aucun bateau</div>
                      </div>
                    </Popup>
                  </Marker>
                );
              })}

              {/* Boats at physical locations (not in config mode) */}
              {!configMode && locations.map(loc => {
                const locTransfers = transfersByLocation[loc.value] || [];
                if (locTransfers.length === 0) return null;

                return locTransfers.map((t, idx) => {
                  const yacht = yachts.find(y => y.id === t.yacht_id);
                  const isUrgent = urgentTransfers.includes(t.id);
                  const isTracked = trackedTransfers.includes(t.id);
                  // Offset multiple boats slightly
                  const offset = idx * 0.003;

                  return (
                    <Marker
                      key={t.id}
                      position={[loc.lat + offset, loc.lng + offset]}
                      icon={createBoatIcon(loc.color, isUrgent)}
                      eventHandlers={{ click: () => setDetailTransfer(t) }}
                    >
                      <Popup>
                        <div style={{ textAlign: 'center', minWidth: 120 }}>
                          <div style={{ fontWeight: 600, marginBottom: 4 }}>{yacht?.name || t.yacht_name}</div>
                          <div style={{ fontSize: 12, color: '#666' }}>{t.client_name}</div>
                          <Tag color={loc.color} style={{ marginTop: 4 }}>{loc.label}</Tag>
                          {t.landing_time && <div style={{ fontSize: 11, marginTop: 4 }}>✈️ {t.landing_time}</div>}
                          {isTracked && <div style={{ fontSize: 11, color: '#ff4d4f', marginTop: 2 }}>🔔 Suivi actif</div>}
                        </div>
                      </Popup>
                    </Marker>
                  );
                });
              })}

              {/* Boats in transit - not in config mode */}
              {!configMode && transfersInTransit.map(t => {
                const state = TRANSIT_STATES.find(s => s.value === t.current_location);
                if (!state || !state.from || !state.to) return null;

                const fromLoc = locations.find(l => l.value === state.from);
                const toLoc = locations.find(l => l.value === state.to);
                if (!fromLoc || !toLoc) return null;

                const isUrgent = urgentTransfers.includes(t.id);

                return (
                  <TransitMarker
                    key={t.id}
                    transfer={t}
                    fromLoc={fromLoc}
                    toLoc={toLoc}
                    color={state.color}
                    isUrgent={isUrgent}
                    onClick={() => setDetailTransfer(t)}
                    clientName={t.client_name}
                  />
                );
              })}

              {/* EN_VOL and ATTERRI are only shown in the flight panel, not on the map */}
            </MapContainer>

            {/* Flight Panel - Bottom Left (EN_VOL and ATTERRI) */}
            {transfersSpecialState.length > 0 && (
              <div style={{
                position: 'absolute',
                bottom: 16,
                left: 16,
                background: 'rgba(0, 0, 0, 0.85)',
                borderRadius: radius.lg,
                padding: spacing.md,
                minWidth: 280,
                maxWidth: 320,
                zIndex: 1000,
                backdropFilter: 'blur(8px)',
                border: '1px solid rgba(255,255,255,0.1)',
              }}>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                  marginBottom: spacing.sm,
                  paddingBottom: spacing.sm,
                  borderBottom: '1px solid rgba(255,255,255,0.15)',
                }}>
                  <span style={{ fontSize: 18 }}>✈️</span>
                  <Text strong style={{ color: '#fff', fontSize: 13 }}>
                    Vols ({transfersSpecialState.length})
                  </Text>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                  {transfersSpecialState.map(t => {
                    const isEnVol = t.current_location === 'EN_VOL';
                    const bgColor = isEnVol ? 'rgba(250, 173, 20, 0.15)' : 'rgba(82, 196, 26, 0.15)';
                    const borderColor = isEnVol ? 'rgba(250, 173, 20, 0.3)' : 'rgba(82, 196, 26, 0.3)';
                    const hoverBg = isEnVol ? 'rgba(250, 173, 20, 0.25)' : 'rgba(82, 196, 26, 0.25)';

                    return (
                      <div
                        key={t.id}
                        onClick={() => setDetailTransfer(t)}
                        style={{
                          background: bgColor,
                          borderRadius: radius.md,
                          padding: '10px 12px',
                          cursor: 'pointer',
                          border: `1px solid ${borderColor}`,
                          transition: 'all 0.2s',
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.background = hoverBg;
                          e.currentTarget.style.transform = 'translateX(4px)';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.background = bgColor;
                          e.currentTarget.style.transform = 'translateX(0)';
                        }}
                      >
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                          <div>
                            <div style={{ color: '#fff', fontWeight: 600, fontSize: 13, marginBottom: 2 }}>
                              {isEnVol ? '✈️' : '🛬'} {t.client_name}
                            </div>
                            <div style={{ color: 'rgba(255,255,255,0.6)', fontSize: 11 }}>
                              {t.yacht_name}
                            </div>
                          </div>
                          {t.landing_time && (
                            <Tag color={isEnVol ? 'orange' : 'green'} style={{ margin: 0, fontSize: 11, fontWeight: 600 }}>
                              {isEnVol ? '🛬' : '✅'} {t.landing_time}
                            </Tag>
                          )}
                        </div>
                        {t.flight_number && (
                          <div style={{
                            marginTop: 6,
                            display: 'flex',
                            alignItems: 'center',
                            gap: 6,
                          }}>
                            <span style={{ color: 'rgba(255,255,255,0.5)', fontSize: 10 }}>Vol:</span>
                            <span style={{ color: isEnVol ? '#faad14' : '#52c41a', fontSize: 11, fontWeight: 600 }}>{t.flight_number}</span>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>

          {/* Location Quick Filter Bar */}
          <div style={{ marginTop: spacing.sm, display: 'flex', flexWrap: 'wrap', gap: 6, padding: `0 ${spacing.sm}` }}>
            {locations.filter(loc => transfersByLocation[loc.value]?.length > 0).map(loc => (
              <Tag
                key={loc.value}
                color={selectedLocation === loc.value ? loc.color : 'default'}
                style={{ cursor: 'pointer', margin: 0 }}
                onClick={() => setSelectedLocation(selectedLocation === loc.value ? null : loc.value)}
              >
                {loc.label} ({transfersByLocation[loc.value]?.length})
              </Tag>
            ))}
            {transfersInTransit.length > 0 && (
              <Tag color="orange" style={{ margin: 0 }}>
                🚤 En transit ({transfersInTransit.length})
              </Tag>
            )}
            {selectedLocation && (
              <Tag style={{ cursor: 'pointer', margin: 0 }} onClick={() => setSelectedLocation(null)}>
                Tout voir ×
              </Tag>
            )}
          </div>
        </div>

        {/* RIGHT: Transfer List */}
        <div style={{
          background: '#fff',
          borderRadius: radius.xl,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          boxShadow: shadows.md,
        }}>
          {/* List Header */}
          <div style={{ padding: spacing.md, borderBottom: `1px solid ${colors.borderSubtle}` }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: spacing.sm }}>
              <Text strong>Transferts actifs</Text>
              <Space size={4}>
                <Button size="small" icon={<ReloadOutlined />} onClick={fetchTransfers} loading={loading} type="text" />
                <Button size="small" type="primary" icon={<PlusOutlined />} onClick={handleAdd} />
              </Space>
            </div>
            <Input
              placeholder="Rechercher client, bateau, vol..."
              prefix={<SearchOutlined style={{ color: '#999' }} />}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              allowClear
              size="small"
              style={{ borderRadius: radius.md }}
            />
          </div>

          {/* Transfer List */}
          <div style={{ flex: 1, overflowY: 'auto', padding: spacing.sm }}>
            {filteredTransfers.length === 0 ? (
              <div style={{ textAlign: 'center', padding: spacing.xl, color: '#999' }}>
                {searchQuery ? 'Aucun résultat' : 'Aucun transfert en cours'}
              </div>
            ) : (
              filteredTransfers.map(transfer => {
                const loc = ALL_LOCATIONS.find(l => l.value === transfer.current_location);
                const isHighlighted = selectedLocation && transfer.current_location === selectedLocation;
                const isHovered = hoveredTransferId === transfer.id;
                const isTracked = trackedTransfers.includes(transfer.id);
                const isUrgent = urgentTransfers.includes(transfer.id);

                return (
                  <div
                    key={transfer.id}
                    onClick={() => setDetailTransfer(transfer)}
                    style={{
                      background: isUrgent ? 'rgba(255,77,79,0.1)' : isHovered ? `${loc?.color}20` : isHighlighted ? `${loc?.color}10` : '#fafafa',
                      borderRadius: radius.md,
                      padding: spacing.sm,
                      marginBottom: spacing.xs,
                      borderLeft: `4px solid ${isUrgent ? '#ff4d4f' : loc?.color || '#1890ff'}`,
                      cursor: 'pointer',
                      transition: 'all 0.15s',
                      border: isUrgent ? '1px solid rgba(255,77,79,0.4)' : isHovered || isHighlighted ? `1px solid ${loc?.color}40` : '1px solid transparent',
                      transform: isHovered ? 'translateX(4px)' : 'translateX(0)',
                      boxShadow: isHovered ? shadows.md : 'none',
                      position: 'relative',
                    }}
                    onMouseEnter={() => setHoveredTransferId(transfer.id)}
                    onMouseLeave={() => setHoveredTransferId(null)}
                  >
                    {isUrgent && (
                      <div style={{
                        position: 'absolute',
                        top: 8,
                        right: 8,
                        width: 10,
                        height: 10,
                        borderRadius: '50%',
                        background: '#ff4d4f',
                        animation: 'glow 1s ease-in-out infinite alternate',
                      }} />
                    )}

                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: 4 }}>
                      <div style={{ flex: 1 }}>
                        <Text strong style={{ fontSize: 12, display: 'block' }}>{transfer.client_name}</Text>
                        <Text type="secondary" style={{ fontSize: 10 }}>{transfer.yacht_name}</Text>
                      </div>
                      <Space size={0} onClick={(e) => e.stopPropagation()}>
                        <Tooltip title={isTracked ? 'Ne plus suivre' : 'Suivre ce transfert'}>
                          <Button
                            type="text"
                            size="small"
                            icon={<BellOutlined style={{ fontSize: 12, color: isTracked ? '#ff4d4f' : '#999' }} />}
                            onClick={() => toggleTrackTransfer(transfer.id)}
                            style={{
                              background: isTracked ? 'rgba(255,77,79,0.1)' : 'transparent',
                              borderRadius: 4,
                            }}
                          />
                        </Tooltip>
                        <Button type="text" size="small" icon={<EditOutlined style={{ fontSize: 10 }} />} onClick={() => handleEdit(transfer)} />
                        <Popconfirm title="Supprimer?" onConfirm={() => handleDelete(transfer.id)} okText="Oui" cancelText="Non">
                          <Button type="text" size="small" icon={<DeleteOutlined style={{ fontSize: 10, color: '#ff4d4f' }} />} />
                        </Popconfirm>
                      </Space>
                    </div>

                    <div style={{ display: 'flex', gap: 8, fontSize: 10, color: '#666', flexWrap: 'wrap', alignItems: 'center' }}>
                      {transfer.transfer_type && <Tag color="blue" style={{ fontSize: 9, margin: 0 }}>{transfer.transfer_type}</Tag>}
                      {transfer.landing_time && (
                        <span style={{
                          color: isUrgent ? '#ff4d4f' : '#666',
                          fontWeight: isUrgent ? 600 : 400,
                        }}>
                          ✈️ {transfer.landing_time}
                        </span>
                      )}
                      {isTracked && <Tag color="red" style={{ fontSize: 9, margin: 0 }}>🔔 Suivi</Tag>}
                      {TRANSIT_STATES.find(s => s.value === transfer.current_location) && (
                        <Tag color={loc?.color} style={{ fontSize: 9, margin: 0 }}>
                          {loc?.label}
                        </Tag>
                      )}
                    </div>

                    <Select
                      value={transfer.current_location}
                      onChange={(val) => updateLocation(transfer.id, val)}
                      size="small"
                      style={{ width: '100%', marginTop: 6 }}
                      dropdownStyle={{ minWidth: 200 }}
                      onClick={(e) => e.stopPropagation()}
                    >
                      <Select.OptGroup label="📍 Positions">
                        {locations.map(l => (
                          <Option key={l.value} value={l.value}>
                            <EnvironmentOutlined style={{ color: l.color, marginRight: 4 }} />
                            {l.label}
                          </Option>
                        ))}
                      </Select.OptGroup>
                      <Select.OptGroup label="🚀 En transit">
                        {TRANSIT_STATES.map(s => (
                          <Option key={s.value} value={s.value}>
                            <span style={{ color: s.color }}>{s.label}</span>
                          </Option>
                        ))}
                      </Select.OptGroup>
                    </Select>
                  </div>
                );
              })
            )}

            {completedToday > 0 && (
              <div style={{ background: '#f6ffed', borderRadius: radius.md, padding: spacing.sm, marginTop: spacing.sm, textAlign: 'center' }}>
                <Text type="secondary" style={{ fontSize: 10 }}>✅ {completedToday} terminé(s) aujourd'hui</Text>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Modal */}
      <Modal
        title={editingTransfer ? 'Modifier' : 'Nouveau transfert'}
        open={modalVisible}
        onCancel={() => { setModalVisible(false); form.resetFields(); }}
        footer={null}
        width={700}
      >
        <Form form={form} layout="vertical" onFinish={handleSave} size="small">
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <Form.Item name="client_name" label="Client" rules={[{ required: true }]}>
              <Input />
            </Form.Item>
            <Form.Item name="transfer_type" label="Trajet">
              <AutoComplete
                options={TRANSFER_ROUTES.map(r => ({ value: r }))}
                placeholder="SXM/SBH..."
                filterOption={(input, option) => option.value.toLowerCase().includes(input.toLowerCase())}
              />
            </Form.Item>
            <Form.Item name="yacht_id" label="Bateau" rules={[{ required: true }]}>
              <Select showSearch optionFilterProp="children">
                {yachts.map(y => <Option key={y.id} value={y.id}>{y.brand} {y.name}</Option>)}
              </Select>
            </Form.Item>
            <Form.Item name="crew" label="Équipage">
              <Select mode="multiple" showSearch optionFilterProp="children">
                {employees.filter(e => ['Skipper', 'SKIPPER', 'Capitaine', 'CAPITAINE', 'Marin', 'MARIN', 'marin'].includes(e.role)).map(e => (
                  <Option key={e.id} value={e.name}>{e.name} ({e.role})</Option>
                ))}
              </Select>
            </Form.Item>
            <Form.Item name="flight_number" label="Vol">
              <Input placeholder="AF1234" />
            </Form.Item>
            <Form.Item name="landing_time" label="Atterrissage">
              <TimePicker format="HH:mm" style={{ width: '100%' }} />
            </Form.Item>
            <Form.Item name="transport_sxm" label="Transport SXM">
              <Select allowClear>
                {TRANSPORT_SXM_OPTIONS.map(o => <Option key={o.value} value={o.value}>{o.label}</Option>)}
              </Select>
            </Form.Item>
            <Form.Item noStyle shouldUpdate={(prev, cur) => prev.transport_sxm !== cur.transport_sxm}>
              {({ getFieldValue }) => getFieldValue('transport_sxm') === 'AUTRE' ? (
                <Form.Item name="transport_sxm_custom" label="Préciser">
                  <Input />
                </Form.Item>
              ) : <div />}
            </Form.Item>
            <Form.Item name="transport_sbh" label="Transport SBH">
              <Select allowClear>
                {TRANSPORT_SBH_OPTIONS.map(o => <Option key={o.value} value={o.value}>{o.label}</Option>)}
              </Select>
            </Form.Item>
            <Form.Item name="transport_contact" label="Contact transport">
              <Input />
            </Form.Item>
          </div>

          <Divider style={{ margin: '12px 0' }}>Contacts Hotel</Divider>
          <Form.List name="hotel_contacts">
            {(fields, { add, remove }) => (
              <>
                {fields.map(({ key, name, ...rest }) => (
                  <Space key={key} style={{ display: 'flex', marginBottom: 4 }} align="baseline">
                    <Form.Item {...rest} name={[name, 'name']} style={{ marginBottom: 0 }}>
                      <Input placeholder="Hotel/Concierge" style={{ width: 140 }} />
                    </Form.Item>
                    <Form.Item {...rest} name={[name, 'phone']} style={{ marginBottom: 0 }}>
                      <Input placeholder="Tél" prefix={<PhoneOutlined />} style={{ width: 130 }} />
                    </Form.Item>
                    <DeleteOutlined onClick={() => remove(name)} style={{ color: '#ff4d4f' }} />
                  </Space>
                ))}
                <Button type="dashed" onClick={() => add()} size="small" icon={<PlusOutlined />}>Contact</Button>
              </>
            )}
          </Form.List>

          <Divider style={{ margin: '12px 0' }}>Autres</Divider>
          <Form.Item name="special_notes" label="Notes">
            <TextArea rows={2} />
          </Form.Item>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <Form.Item name="current_location" label="État">
              <Select dropdownStyle={{ minWidth: 200 }}>
                <Select.OptGroup label="📍 Positions">
                  {locations.map(l => (
                    <Option key={l.value} value={l.value}>
                      <Tag color={l.color} style={{ marginRight: 4 }}>{l.label}</Tag>
                    </Option>
                  ))}
                </Select.OptGroup>
                <Select.OptGroup label="🚀 En transit">
                  {TRANSIT_STATES.map(s => (
                    <Option key={s.value} value={s.value}>
                      <span style={{ color: s.color }}>{s.label}</span>
                    </Option>
                  ))}
                </Select.OptGroup>
              </Select>
            </Form.Item>
            <Form.Item name="status" label="Statut">
              <Select>
                <Option value="pending">En attente</Option>
                <Option value="in_progress">En cours</Option>
                <Option value="completed">Terminé</Option>
              </Select>
            </Form.Item>
          </div>
          <Form.Item name="transfer_date" hidden><Input /></Form.Item>

          <div style={{ textAlign: 'right', marginTop: 16 }}>
            <Space>
              <Button onClick={() => { setModalVisible(false); form.resetFields(); }}>Annuler</Button>
              <Button type="primary" htmlType="submit">{editingTransfer ? 'Modifier' : 'Créer'}</Button>
            </Space>
          </div>
        </Form>
      </Modal>

      {/* WhatsApp Group Config Modal */}
      <Modal
        title="📱 Configuration WhatsApp Groupe"
        open={whatsappConfigVisible}
        onCancel={() => setWhatsappConfigVisible(false)}
        onOk={saveWhatsappGroupNumber}
        okText="Sauvegarder"
        cancelText="Annuler"
        width={400}
      >
        <div style={{ marginBottom: 16 }}>
          <Text type="secondary" style={{ fontSize: 12 }}>
            Entrez le numéro WhatsApp pour recevoir les notifications de transfert.
            Ce numéro sera utilisé pour envoyer les messages comme "Client X a atterri", etc.
          </Text>
        </div>
        <Input
          placeholder="+590690XXXXXX"
          value={whatsappGroupNumber}
          onChange={(e) => setWhatsappGroupNumber(e.target.value)}
          prefix={<span style={{ fontSize: 16 }}>📱</span>}
          size="large"
          style={{ marginBottom: 12 }}
        />
        <div style={{ fontSize: 11, color: '#666' }}>
          <div>💡 <strong>Conseil:</strong> Utilisez le numéro d'un membre du groupe qui peut transférer les messages.</div>
          <div style={{ marginTop: 4 }}>Format: +590690123456 ou 0690123456</div>
        </div>
        {whatsappGroupNumber && (
          <div style={{ marginTop: 12, padding: 8, background: '#f0fdf4', borderRadius: 6, border: '1px solid #bbf7d0' }}>
            <Text style={{ fontSize: 11, color: '#16a34a' }}>
              ✅ Numéro configuré: {whatsappGroupNumber}
            </Text>
          </div>
        )}
      </Modal>

      {/* Detail Drawer */}
      <Drawer
        title={
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ fontSize: 20 }}>🚤</span>
            <div>
              <div style={{ fontWeight: 600 }}>{detailTransfer?.client_name}</div>
              {detailTransfer?.transfer_type && (
                <Tag color="blue" style={{ marginTop: 2 }}>{detailTransfer.transfer_type}</Tag>
              )}
            </div>
          </div>
        }
        placement="right"
        open={!!detailTransfer}
        onClose={() => setDetailTransfer(null)}
        width={340}
        styles={{
          body: { padding: spacing.md, background: '#fafafa' },
          header: { borderBottom: `1px solid ${colors.borderSubtle}` },
        }}
        extra={
          <Button
            type="primary"
            icon={<EditOutlined />}
            size="small"
            onClick={() => { handleEdit(detailTransfer); setDetailTransfer(null); }}
          >
            Modifier
          </Button>
        }
      >
        {detailTransfer && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <div style={{ background: '#fff', padding: 12, borderRadius: radius.md, boxShadow: shadows.sm }}>
              <Text type="secondary" style={{ fontSize: 10, textTransform: 'uppercase' }}>Bateau</Text>
              <div style={{ fontWeight: 600, fontSize: 14, marginTop: 2 }}>{detailTransfer.yacht_name || '-'}</div>
            </div>

            <div style={{ background: '#fff', padding: 12, borderRadius: radius.md, boxShadow: shadows.sm }}>
              <Text type="secondary" style={{ fontSize: 10, textTransform: 'uppercase' }}>Position</Text>
              <div style={{ marginTop: 4 }}>
                <Tag color={ALL_LOCATIONS.find(l => l.value === detailTransfer.current_location)?.color} style={{ fontSize: 12 }}>
                  {ALL_LOCATIONS.find(l => l.value === detailTransfer.current_location)?.label || detailTransfer.current_location}
                </Tag>
              </div>
            </div>

            {(detailTransfer.flight_number || detailTransfer.landing_time) && (
              <div style={{ background: '#fff', padding: 12, borderRadius: radius.md, boxShadow: shadows.sm }}>
                <Text type="secondary" style={{ fontSize: 10, textTransform: 'uppercase' }}>Vol</Text>
                <div style={{ fontWeight: 600, marginTop: 2, display: 'flex', alignItems: 'center', gap: 8 }}>
                  {detailTransfer.flight_number && <span>{detailTransfer.flight_number}</span>}
                  {detailTransfer.landing_time && <Tag color="blue">✈️ {detailTransfer.landing_time}</Tag>}
                </div>
              </div>
            )}

            {detailTransfer.crew && (Array.isArray(detailTransfer.crew) ? detailTransfer.crew.length > 0 : detailTransfer.crew) && (
              <div style={{ background: '#fff', padding: 12, borderRadius: radius.md, boxShadow: shadows.sm }}>
                <Text type="secondary" style={{ fontSize: 10, textTransform: 'uppercase' }}>Équipage</Text>
                <div style={{ fontWeight: 600, marginTop: 2 }}>
                  {Array.isArray(detailTransfer.crew) ? detailTransfer.crew.join(', ') : detailTransfer.crew}
                </div>
              </div>
            )}

            {(detailTransfer.transport_sxm || detailTransfer.transport_sbh) && (
              <div style={{ background: '#fff', padding: 12, borderRadius: radius.md, boxShadow: shadows.sm }}>
                <Text type="secondary" style={{ fontSize: 10, textTransform: 'uppercase' }}>Transport</Text>
                <div style={{ marginTop: 4, display: 'flex', flexDirection: 'column', gap: 6 }}>
                  {detailTransfer.transport_sxm && (
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Text type="secondary" style={{ fontSize: 11 }}>SXM:</Text>
                      <Tag color="orange">{getSxmTransportLabel(detailTransfer)}</Tag>
                    </div>
                  )}
                  {detailTransfer.transport_sbh && (
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Text type="secondary" style={{ fontSize: 11 }}>SBH:</Text>
                      <Tag color="green">{TRANSPORT_SBH_OPTIONS.find(o => o.value === detailTransfer.transport_sbh)?.label || '-'}</Tag>
                    </div>
                  )}
                </div>
              </div>
            )}

            {detailTransfer.transport_contact && (
              <div style={{ background: '#fff', padding: 12, borderRadius: radius.md, boxShadow: shadows.sm }}>
                <Text type="secondary" style={{ fontSize: 10, textTransform: 'uppercase' }}>Contact Transport</Text>
                <div style={{ fontWeight: 600, marginTop: 2, display: 'flex', alignItems: 'center', gap: 6 }}>
                  <PhoneOutlined style={{ color: '#1890ff' }} />
                  {detailTransfer.transport_contact}
                </div>
              </div>
            )}

            {detailTransfer.hotel_contacts && detailTransfer.hotel_contacts.length > 0 && (
              <div style={{ background: '#e6f7ff', padding: 12, borderRadius: radius.md }}>
                <Text type="secondary" style={{ fontSize: 10, textTransform: 'uppercase' }}>Contacts Hotel</Text>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 4, marginTop: 4 }}>
                  {detailTransfer.hotel_contacts.map((contact, idx) => (
                    <div key={idx} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12 }}>
                      <PhoneOutlined style={{ color: '#1890ff', fontSize: 11 }} />
                      <span style={{ fontWeight: 500 }}>{contact.name}:</span>
                      <span>{contact.phone}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* WhatsApp Actions Section */}
            {(() => {
              const actions = getContextualActions(detailTransfer, locations);
              const whatsappGroup = localStorage.getItem(WHATSAPP_GROUP_CONFIG_KEY);

              if (actions.length === 0) return null;

              const callActions = actions.filter(a => a.type === 'call');
              const messageActions = actions.filter(a => a.type === 'message');

              return (
                <div style={{ background: '#f0fdf4', padding: 12, borderRadius: radius.md, border: '1px solid #bbf7d0' }}>
                  <Text type="secondary" style={{ fontSize: 10, textTransform: 'uppercase', color: '#16a34a' }}>
                    📱 Actions rapides
                  </Text>

                  {callActions.length > 0 && (
                    <div style={{ marginTop: 8 }}>
                      <Text style={{ fontSize: 10, color: '#666' }}>Appeler via WhatsApp:</Text>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 4 }}>
                        {callActions.map((action, idx) => (
                          <Button
                            key={idx}
                            size="small"
                            type="primary"
                            style={{
                              background: action.color,
                              borderColor: action.color,
                              fontSize: 11,
                              display: 'flex',
                              alignItems: 'center',
                              gap: 4,
                            }}
                            onClick={() => openWhatsAppCall(action.phone)}
                          >
                            {action.icon} {action.label}
                          </Button>
                        ))}
                      </div>
                    </div>
                  )}

                  {messageActions.length > 0 && (
                    <div style={{ marginTop: 10 }}>
                      <Text style={{ fontSize: 10, color: '#666' }}>Notifier le groupe:</Text>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginTop: 4 }}>
                        {messageActions.map((action, idx) => (
                          <Button
                            key={idx}
                            size="small"
                            style={{
                              background: `${action.color}20`,
                              borderColor: action.color,
                              color: action.color,
                              fontSize: 11,
                              display: 'flex',
                              alignItems: 'center',
                              gap: 4,
                              justifyContent: 'flex-start',
                            }}
                            onClick={() => {
                              if (whatsappGroup) {
                                sendWhatsAppMessage(whatsappGroup, action.message);
                              } else {
                                message.warning('Configurez d\'abord le numéro WhatsApp groupe dans les paramètres');
                              }
                            }}
                          >
                            {action.icon} {action.label}
                          </Button>
                        ))}
                      </div>
                      {!whatsappGroup && (
                        <div style={{ marginTop: 6, fontSize: 10, color: '#f59e0b' }}>
                          ⚠️ Numéro groupe non configuré
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })()}

            {detailTransfer.special_notes && (
              <div style={{ background: '#fff7e6', padding: 12, borderRadius: radius.md }}>
                <Text type="secondary" style={{ fontSize: 10, textTransform: 'uppercase' }}>Notes</Text>
                <div style={{ whiteSpace: 'pre-wrap', marginTop: 4, fontSize: 12 }}>{detailTransfer.special_notes}</div>
              </div>
            )}

            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingTop: 8, borderTop: `1px solid ${colors.borderSubtle}` }}>
              <Tag color={
                detailTransfer.status === 'completed' ? 'green' :
                detailTransfer.status === 'in_progress' ? 'blue' : 'default'
              }>
                {detailTransfer.status === 'completed' ? '✅ Terminé' :
                 detailTransfer.status === 'in_progress' ? '🔄 En cours' : '⏳ En attente'}
              </Tag>
              <Text type="secondary" style={{ fontSize: 11 }}>
                {detailTransfer.transfer_date && dayjs(detailTransfer.transfer_date).format('DD/MM/YYYY')}
              </Text>
            </div>
          </div>
        )}
      </Drawer>
    </div>
  );
};

export default TransferTracking;
